/**
 * CAMERAS — Lens Cinema Live + retained frames + bind management.
 * Soft≠Live: Live lamp only for authoritative publishing/viewable sessions.
 */
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico, Alert, SOFT, SPRING, go, stay, hashSearchParams, Field } from "./ui.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { path, useApi } from "./api.js";
import { useSession } from "./session.js";
import { hasExplicitSaSeat, hasTenantAdminSeat } from "./cap-complete.js";
import { opsCamerasSeatOf, opsCamerasDevicesOf } from "./ops-cameras-desk.js";
import { CameraSettingsDesk } from "./camera-settings-desk.js";
import { CamerasLiveWall } from "./cameras-live-wall.js";
import { DeviceCamerasLive } from "./device-cameras.js";
import { EmptyAction, ReadinessState, SearchPicker, TaskStrip } from "./layered-console.js";

const TABS = [
  { id: "live", label: "Live", verb: "Cinema", say: "Live means the picture is playing. A ticket is not live." },
  { id: "retained", label: "Retained", verb: "Evidence", say: "Kept pictures. Open a frame to see what was recorded." },
  { id: "manage", label: "Register & bind", verb: "Manage", say: "Choose a board, find a lens, test a frame, then bind." },
];

function textOf(...values) {
  for (const value of values) {
    const text = typeof value === "string" ? value.trim() : "";
    if (text) return text;
  }
  return "";
}

function reportsOf(row) {
  const report = row && (row.ai_report || row.analysis || row.inference || row.ai);
  const obj = report && typeof report === "object" && !Array.isArray(report) ? report : {};
  const summary = textOf(
    typeof report === "string" ? report : "",
    row && row.ai_summary,
    row && row.inference_summary,
    obj.summary,
    obj.report,
    obj.caption,
  );
  const detections = Array.isArray(row && row.detections)
    ? row.detections
    : Array.isArray(obj.detections) ? obj.detections : [];
  const confidence = Number(obj.confidence != null ? obj.confidence : row && row.ai_confidence);
  return {
    active: !!(summary || detections.length || row && (row.ai_active === true || row.inference_active === true)),
    summary,
    detections,
    confidence: Number.isFinite(confidence) ? confidence : null,
    model: textOf(obj.model, row && row.model_name, row && row.ai_model),
    ref: textOf(row && row.ai_report_ref, obj.report_id, obj.proof_assessment_id),
  };
}

function framesOf(data) {
  const rows = (data && (data.items || data.frames || data.snapshots)) || [];
  return Array.isArray(rows) ? rows.map((row, i) => {
    const id = String(row.snapshot_id || row.id || ("frame-" + i));
    const gone = !!(row.expired || row.gone || row.gone_at || row.is_expired);
    let image = textOf(row.thumb_url, row.image_url);
    if (!image && !gone) {
      try { image = path("get__ops_cameras_frames_p_image", { pathParams: [id] }); } catch { image = ""; }
    }
    return {
      ...row,
      id,
      gone,
      image,
      camera: textOf(row.camera_label, row.camera_name, row.camera_id, row.device_id) || "Camera",
      place: textOf(row.zone, row.site, row.device_label),
      at: textOf(row.captured_at, row.created_at, row.at).slice(0, 19).replace("T", " "),
      report: reportsOf(row),
    };
  }) : [];
}

function ReportRail({ frame }) {
  if (!frame) {
    return html`<aside className="cameras-v1__report cameras-v1__report--empty">
      <b>Select a frame</b>
      <p>Retained frames keep their evidence here. Empty stays empty.</p>
    </aside>`;
  }
  const r = frame.report;
  return html`<aside className="cameras-v1__report" data-ai=${r.active ? "1" : "0"}>
    <header><div><p className="eyebrow">Retained evidence</p><h2>${frame.camera}</h2></div>
      <span>${r.active ? "AI report" : "No AI report"}</span></header>
    <div className="cameras-v1__report-meta">
      <span><small>Captured</small><b>${frame.at || "Not reported"}</b></span>
      <span><small>Placement</small><b>${frame.place || "Not reported"}</b></span>
      <span><small>Model</small><b>${r.model || "Not reported"}</b></span>
      <span><small>Confidence</small><b>${r.confidence == null ? "Not reported" : Math.round(r.confidence * (r.confidence <= 1 ? 100 : 1)) + "%"}</b></span>
    </div>
    <section><small>Generated report</small>
      <p>${r.summary || "This frame has no AI-generated narrative. The console will not manufacture one."}</p>
    </section>
    <section><small>Detections</small>
      <div className="cameras-v1__detections">
        ${r.detections.length ? r.detections.map((item, i) => {
          const obj = item && typeof item === "object" ? item : {};
          const label = typeof item === "string" ? item : textOf(obj.label, obj.class_name, obj.name) || "Detection";
          const score = Number(obj.confidence != null ? obj.confidence : obj.score);
          return html`<span key=${i}><i/><b>${label}</b><em>${Number.isFinite(score) ? Math.round(score * (score <= 1 ? 100 : 1)) + "%" : "observed"}</em></span>`;
        }) : html`<p>No detections reported.</p>`}
      </div>
    </section>
    <footer><span>Frame ${frame.id}</span>
      <button type="button" className="btn btn--ghost btn--xs"
        onClick=${() => confirmLeaveSeat({
          fromRoute: "/cameras/retained",
          targetPath: "/response/cases",
          objectLabel: "Response",
        })}>Open incidents</button></footer>
  </aside>`;
}

function RetainedWall({ toast }) {
  const posture = useApi(path("get__ops_cameras_status"));
  const seat = opsCamerasSeatOf(posture.loading || !posture.data ? {} : posture.data);
  const framesApi = useApi(seat.framesAllowed
    ? path("get__ops_cameras_frames", { query: { limit: seat.framesLimit || 36 } }) : "");
  const frames = useMemo(() => framesOf(framesApi.data).filter((f) => !f.gone), [framesApi.data]);
  const [selectedId, setSelectedId] = useState("");
  const selected = frames.find((f) => f.id === selectedId) || frames[0] || null;
  const aiN = frames.filter((f) => f.report.active).length;

  return html`<div className="cameras-v1__workspace">
    <section className="cameras-v1__wall">
      <div className="cameras-v1__wall-head"><div><span>Retained frames</span>
        <small>Latest retained frame per capture — not a fabricated continuous stream.</small></div>
        <div><em>${frames.length} frames</em><em data-ai="1">${aiN} AI reports</em>
          <button type="button" className="btn btn--ghost btn--xs" disabled=${posture.loading || framesApi.loading}
            onClick=${() => {
              posture.reload && posture.reload();
              framesApi.reload && framesApi.reload();
              toast && toast("Retained wall refreshed", "go");
            }}><${Ico} d="refresh" w=${12}/> Refresh</button></div>
      </div>
      ${posture.error ? html`<${Alert} tone="warn" title="Camera posture unavailable" say=${posture.error}/>` : null}
      ${!posture.loading && !seat.framesAllowed
        ? html`<${Alert} tone="info" title="Retained wall sealed" say="This seat cannot read camera frames for the current tenant."/>`
        : null}
      <div className="cameras-v1__grid">
        ${frames.length ? frames.map((frame, i) => html`<${M.button} key=${frame.id} type="button"
          className="cameras-v1__tile" data-on=${selected && selected.id === frame.id ? "1" : "0"}
          data-ai=${frame.report.active ? "1" : "0"} onClick=${() => setSelectedId(frame.id)}
          initial=${{ opacity: 0, scale: .96, y: 8 }} animate=${{ opacity: 1, scale: 1, y: 0 }}
          transition=${{ ...SPRING, delay: Math.min(i * .035, .28) }}>
          <span className="cameras-v1__media">
            ${frame.image ? html`<img src=${frame.image} alt=${"Latest frame from " + frame.camera} loading="lazy"/>`
              : html`<i><${Ico} d="camera" w=${24}/></i>`}
            <b>${frame.report.active ? "AI" : "Retained"}</b>
          </span>
          <span className="cameras-v1__tile-foot"><span><b>${frame.camera}</b><small>${frame.place || frame.at || "Placement not reported"}</small></span><i/></span>
        <//>`) : html`<div className="cameras-v1__no-frames house-rack__empty">
          <i/><b>No retained camera frames</b>
          <span>Capture history is empty for this seat. Empty stays empty.</span>
          <button type="button" className="btn btn--go btn--sm" onClick=${() => go("/cameras/live")}>Open live cinema</button>
        </div>`}
      </div>
    </section>
    <${ReportRail} frame=${selected}/>
  </div>`;
}

function CameraBoardBind({ toast }) {
  const requested = String(hashSearchParams().get("device") || "").trim();
  const devicesApi = useApi(path("get__devices", { query: { limit: 80, skip_cache: 1 } }));
  const [deviceId, setDeviceId] = useState(requested);
  useEffect(() => {
    if (requested && requested !== deviceId) setDeviceId(requested);
  }, [requested]);
  const devices = useMemo(() => opsCamerasDevicesOf(devicesApi.data), [devicesApi.data]);
  const pick = (id) => {
    const next = String(id || "").trim();
    setDeviceId(next);
    stay(next ? ("/cameras/manage?device=" + encodeURIComponent(next)) : "/cameras/manage");
  };
  const board = devices.find((d) => d.id === deviceId) || null;
  const boardOn = !!deviceId;
  const steps = [
    { id: "board", label: "Board", on: boardOn, say: board ? (board.label || board.id) : "Select a board" },
    { id: "discover", label: "Discover", on: false, say: "Optional LAN / P4 search, or pick a bound lens" },
    { id: "camera", label: "Camera", on: false, say: "Pick a found host or an owned lens" },
    { id: "credentials", label: "Credentials", on: false, say: "Username and password stay write-only" },
    { id: "test", label: "Test frame", on: false, say: "Live test-capture, not a Soft still" },
    { id: "bind", label: "Save & bind", on: false, say: "Register then bind to this board" },
  ];
  return html`<section className="cameras-v1__bind cam-flow" data-cmc-cameras-bind="1" data-cmc-cam-flow="1093">
    <header className="cam-flow__head">
      <div>
        <p className="eyebrow">Current object</p>
        <h2>${board ? (board.label || board.id) : "Register & bind"}</h2>
        <p>One job: choose a board, find a lens, test a frame, then bind. Empty stays empty. Consequential writes end in a Receipt.</p>
      </div>
      ${board ? html`<div className="cam-flow__object">
        <small>Board</small><b>${board.id}</b>
        <em>${board.online ? "Online" : (board.status || "Listed")}</em>
      </div>` : null}
    </header>
    <${TaskStrip}
      now=${board ? ("Board " + (board.label || board.id)) : "No board selected"}
      next=${board ? "Discover or pick a lens · Test frame · Save & bind · Receipt." : "Search the plant list — native dropdowns cannot hold 10k boards."}
      actionLabel=${board ? null : null}
    />
    <${ReadinessState}
      label="Bind readiness"
      steps=${[
        { id: "configured", label: "Board", on: boardOn },
        { id: "connected", label: "Discover", on: false },
        { id: "available", label: "Lens", on: false },
        { id: "callable", label: "Test frame", on: false },
        { id: "verified", label: "Bound", on: false },
      ]}
      next=${board ? "Complete Test → Save & bind for a Live receipt." : "Select a board first."}
    />
    <ol className="cam-flow__steps" aria-label="Bind sequence">
      ${steps.map((s, i) => html`<li key=${s.id} data-on=${s.on ? "1" : "0"}>
        <i>${i + 1}</i><b>${s.label}</b><small>${s.say}</small>
      </li>`)}
    </ol>
    <${Field} label="Board">
      <${SearchPicker}
        label="Board to bind"
        placeholder="Search boards…"
        value=${deviceId}
        items=${devices.map((d) => ({
          id: d.id,
          label: d.label || d.id,
          hint: d.id + (d.online ? " · online" : ""),
          status_on: !!d.online,
        }))}
        onChange=${(id) => pick(id)}
        emptySay="No board matches that search."
      />
    <//>
    ${devicesApi.error
      ? html`<${Alert} tone="warn" title="Board list unavailable" say="The board list did not load. Bind stays empty."/>`
      : null}
    ${deviceId
      ? html`<${DeviceCamerasLive} deviceId=${deviceId} toast=${toast}/>`
      : html`<${EmptyAction}
          title="Choose a board first"
          say="Binding needs a real board. Search above, then bind a saved camera — LAN / P4 search is optional."
          actionLabel=${devices.length ? null : "Open Live cinema"}
          onAction=${devices.length ? null : () => stay("/cameras/live")}
        />`}
  </section>`;
}

export default function CamerasPrimary({ sub, toast }) {
  const { me } = useSession();
  const canManage = hasTenantAdminSeat(me) || hasExplicitSaSeat(me);
  const tabs = TABS.filter((t) => t.id !== "manage" || canManage);
  const routeAt = tabs.some((t) => t.id === sub) ? sub : "live";
  const [at, setAt] = useState(routeAt);
  useEffect(() => setAt(routeAt), [routeAt]);
  useEffect(() => {
    if (sub === "manage" && !canManage) stay("/cameras/live");
  }, [sub, canManage]);
  const select = (id) => {
    if (id === "manage" && !canManage) return;
    setAt(id);
    stay("/cameras/" + id);
  };
  const immersive = at === "live";
  const pane = at === "manage" && canManage
    ? html`<div className="cam-workbench house-rack" data-cmc-cam-workbench="1090">
        <${CameraSettingsDesk} toast=${toast}/>
        <${CameraBoardBind} toast=${toast}/>
      </div>`
    : (at === "retained"
      ? html`<${RetainedWall} toast=${toast}/>`
      : html`<${CamerasLiveWall} toast=${toast}/>`);

  /* Live = single immersive page · liquid glass · soft slide-in from Operations. */
  if (immersive) {
    return html`<${M.div}
        className="cameras-cinema-root"
        data-cmc-cameras-ux="tri-zone"
        data-lens-cinema="1"
        data-immersive="1"
        initial=${{ opacity: 0, x: 56 }}
        animate=${{ opacity: 1, x: 0 }}
        transition=${{ duration: 0.42, ease: [.16, 1, .3, 1] }}>
      ${pane}
    <//>`;
  }

  return html`<div className="sheet cameras-v1 desk-craft house-rack" data-cmc-cameras-ux="cameras-v1"
      data-lens-cinema="0" data-immersive="0">
    <div className="wrap cameras-v1__wrap">
      <header className="cameras-v1__hero"><div><p className="eyebrow">Visual operations</p>
        <h1 className="ph">Cameras</h1>
        <p className="psub">${(tabs.find((t) => t.id === at) || tabs[0]).say || "Empty stays empty."}</p></div>
        <button type="button" className="btn btn--go btn--sm" onClick=${() => select("live")}>Back to cinema</button>
      </header>
      <nav className="cameras-v1__tabs">${tabs.map((tab) => html`<button key=${tab.id} type="button"
        data-on=${at === tab.id ? "1" : "0"} onClick=${() => select(tab.id)}><b>${tab.label}</b><small>${tab.verb}</small></button>`)}</nav>
      <main className="cameras-v1__stage"><${AnimatePresence} mode="wait"><${M.div} key=${at}
        initial=${{ opacity: 0, y: 8 }} animate=${{ opacity: 1, y: 0 }}
        exit=${{ opacity: 0, y: -6 }} transition=${SOFT}>${pane}<//><//></main>
    </div>
  </div>`;
}
